import { useCallback, useRef } from 'react';
import {
  Documentation,
  DocumentationFilterInputObject,
  DocumentationScopeInputObject,
  FilterGroupingEnum,
  useEdmDocumentationsIdsLazyQuery,
  useEdmDocumentationsIdsQuery,
} from 'graphql-common';
import {
  EdmDocumentationsQueryVariables,
  useEdmDocumentationsQuery,
} from '@lib/graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { Value } from '@lib/interfaces/form';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

type SelectOption = { value: string; label: string };

type EdmDocumentationsQueryHookProps = ListQueryHookProps & {
  approverId?: string;
  isAiDetectionEnabled?: boolean;
  isViewLocked?: boolean;
  linkedAssetsId?: string;
  linkedFormTemplatesId?: string;
  scope?: DocumentationScopeInputObject;
  skipCategory?: boolean;
};

function getIds(
  filterParams: { [key: string]: Value } | undefined,
  name: string,
) {
  return ((_get(filterParams, name, []) as SelectOption[]) || []).map(
    (o) => o.value,
  );
}

function getLabels(
  filterParams: { [key: string]: Value } | undefined,
  name: string,
) {
  return ((_get(filterParams, name, []) as SelectOption[]) || []).map(
    (o) => o.label,
  );
}

export default function useEdmDocumentationsQueryHook({
  approverId,
  filterParams,
  isAiDetectionEnabled,
  isViewLocked,
  linkedAssetsId,
  linkedFormTemplatesId,
  paginationParams,
  searchQuery,
  scope,
  skip,
  skipCategory,
  skipGetAllIds = true,
  usePrevious = true,
  fetchPolicy = 'cache-and-network',
}: EdmDocumentationsQueryHookProps): ListQueryHookResult<Documentation> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const queryVariables: EdmDocumentationsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    scope,
    isViewLocked: isViewLocked ?? false,
    skipCategory: skipCategory ?? false,
    isAiDetectionEnabled: isAiDetectionEnabled ?? false,
  };
  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const filters: DocumentationFilterInputObject[] = [];
  if (searchQuery) {
    // `categoryName` filter arg is gated by `documentation_management` on the
    // BE — drop it for LimitedRead users to avoid UPGRADE_PLAN_REQUIRED.
    const searchFields = isViewLocked
      ? [
          { fieldName: 'name' },
          { fieldName: 'code' },
          { fieldName: 'tagsName' },
        ]
      : [
          { fieldName: 'name' },
          { fieldName: 'code' },
          { fieldName: 'categoryName' },
          { fieldName: 'tagsName' },
        ];
    filters.push(...createSearchFilters(searchQuery, searchFields));
  }

  // Filter-form values
  const categoryNames = getLabels(filterParams, 'categories');
  const validationStatusValues = getIds(filterParams, 'validationStatus');
  const sectorIds = getIds(filterParams, 'sectors');
  const siteIds = getIds(filterParams, 'sites');
  const areaIds = getIds(filterParams, 'areas');
  const equipmentIds = getIds(filterParams, 'equipment');

  if (approverId) {
    filters.push({
      approverId: {
        predicate: { eq: approverId },
      },
    });
  }
  if (linkedAssetsId) {
    filters.push({
      linkedAssetsId: {
        predicate: { eq: linkedAssetsId },
      },
    });
  } else if (equipmentIds.length) {
    filters.push({
      linkedAssetsId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: equipmentIds },
      },
    });
  }
  if (linkedFormTemplatesId) {
    filters.push({
      linkedFormTemplatesId: {
        predicate: { eq: linkedFormTemplatesId },
      },
    });
  }
  // categoryName/validationStatus/sectorsId/sitesId/linkedAreasId filter args
  // are all gated by `documentation_management` on the BE. Skip them entirely
  // when locked (filter UI is hidden, but stale localStorage values could
  // still trigger UPGRADE_PLAN_REQUIRED).
  if (!isViewLocked && categoryNames.length) {
    filters.push({
      categoryName: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: categoryNames },
      },
    });
  }
  if (!isViewLocked && validationStatusValues.length) {
    filters.push({
      validationStatus: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: validationStatusValues },
      },
    });
  }
  if (!isViewLocked && sectorIds.length) {
    filters.push({
      sectorsId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: sectorIds },
      },
    });
  }
  if (!isViewLocked && siteIds.length) {
    filters.push({
      sitesId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: siteIds },
      },
    });
  }
  if (!isViewLocked && areaIds.length) {
    filters.push({
      linkedAreasId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: areaIds },
      },
    });
  }
  if (filters.length) {
    queryVariables.filters = filters;
  }
  const query = useEdmDocumentationsQuery({
    fetchPolicy,
    variables: queryVariables,
    skip,
  });
  const allIdsQuery = useEdmDocumentationsIdsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      onlyIds: true,
      filters: queryVariables.filters,
    },
    skip: skipGetAllIds,
  });
  const [fetchIdsQuery] = useEdmDocumentationsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIdsVariables = {
    onlyIds: true,
    filters: queryVariables.filters,
  };
  const fetchIdsVariablesRef = useRef(fetchIdsVariables);
  fetchIdsVariablesRef.current = fetchIdsVariables;
  // Stable ref so fetchIds is safe as a useEffect dependency (no render loop).
  const fetchIds = useCallback(
    () => fetchIdsQuery({ variables: fetchIdsVariablesRef.current }),
    [fetchIdsQuery],
  );
  const { loading, data, previousData, error, refetch } = query;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as Documentation[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    allIds: _get(allIdsQuery, ['data', 'data', 'collection'], []),
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    refetchAllIds: allIdsQuery.refetch,
    totalCount,
    totalPages,
  };
}
